import React, { useEffect, useCallback } from "react";
import { useClients } from "../../hooks/useApolloClient";
import {
  selectVaultsList,
  addVaultsDetailsByChainID,
  VaultDetails,
  addVaultsUnderlyingBalanceByAddress,
} from "../../store/vaults";
import { useDispatch, useSelector } from "react-redux";
import { utils, constants, BigNumber } from "ethers";
import { fetchVaultDetails } from '../../utils/fetchVaultDetails';
import { fetchTokenPrice } from '../../utils/fetchTokenPrice';
import { useEthPrices } from "../../hooks/useETHPrices";
import { fetchVaults } from '../../utils/fetchVaults';
import { VaultData } from "../../queries/vault";
import { constructVaultDetails } from "../../utils/constructVaultDetails";
import { fetchUnderlyingBalances } from '../../utils/fetchUnderlyingBalances';
import IGUniPoolABI from '../../abis/arrakis-vaults.json';
import { ContractCallContext } from "ethereum-multicall";
import { allProviders } from '../../constants/providers';
import { fetchVaultReward } from "../../utils/fetchVaultReward";

export const VaultsDetailsLoader = ({ chainId }: { chainId: number }) => {
  const dispatch = useDispatch();
  const { dataClient, uniClient, gaugeClient } = useClients(chainId);
  const vaultsList = useSelector(selectVaultsList);
  const whitelistedVaultsByChainId = vaultsList.filter((v) => v.chainId === chainId);
  const { current: currentEthPrice } = useEthPrices(chainId) || { current: 0 };

  const fetchPrice = useCallback(
    (address: string) => {
      return fetchTokenPrice(uniClient, currentEthPrice, address)
    },
    [currentEthPrice, uniClient]
  )

  const fetchReward = useCallback(
    async (vaultAddress: string) => {
      const vaultInfo = vaultsList.find(
        (v) => utils.getAddress(v.address) === utils.getAddress(vaultAddress)
      );
      if (vaultInfo && vaultInfo.gauge) {
        const data = await fetchVaultReward(
          gaugeClient,
          vaultInfo.gauge.address,
          utils.getAddress(vaultInfo.gauge.rewardToken)
        );
        if (!data) return { reward: constants.Zero, timestamp: 0 };
        const { amount, timestamp } = data;
        return {
          reward: BigNumber.from(amount),
          timestamp: parseInt(timestamp),
        };
      } else return { reward: constants.Zero, timestamp: 0 };
    },
    [gaugeClient, vaultsList]
  );

  const fetchRewardPrice = useCallback(
    async (vaultAddress: string) => {
      const vaultInfo = vaultsList.find(
        (v) => utils.getAddress(v.address) === utils.getAddress(vaultAddress)
      );
      if (vaultInfo && vaultInfo.gauge) {
        return fetchTokenPrice(
          uniClient,
          currentEthPrice,
          utils.getAddress(vaultInfo.gauge.rewardToken)
        );
      } else return 0;
    },
    [currentEthPrice, uniClient, vaultsList]
  );

  const fetchVault = useCallback(
    async (vault: VaultData) => {
      return fetchVaultDetails(chainId, vault, fetchPrice, fetchReward, fetchRewardPrice)
    }, [chainId, fetchPrice, fetchReward, fetchRewardPrice]
  )

  const fetchVaultsByChainId = useCallback(
    () => {
      return fetchVaults(dataClient);
    }, [dataClient]
  )
  
  const fetchVaultsUnderlyingBalances = useCallback(async () => {
    const start = Date.now()
    console.log(
      `fetchiing vaults UNDERLYING balances for chain ${chainId} START at (${Date.now().toString()})}`
    )
    // fetch underlying token balance
    const underlyingBalanceContractCallContext: Array<ContractCallContext> = whitelistedVaultsByChainId.map(vault => {
      return {
        reference: utils.getAddress(vault.address),
        contractAddress: utils.getAddress(vault.address),
        abi: IGUniPoolABI,
        calls: [{ reference: 'underlyingBalanceCall', methodName: 'getUnderlyingBalances', methodParameters: [] }]
      }
    })
    const underlyingBalances = await fetchUnderlyingBalances(allProviders[chainId], underlyingBalanceContractCallContext)
    underlyingBalances.forEach(balance => {
      dispatch(addVaultsUnderlyingBalanceByAddress({
        vaultAddress: utils.getAddress(balance.address),
        balance0: balance.balance0,
        balance1: balance.balance1,
      }))
    })
    console.log(
      `fetching vaults UNDERLYING balances COMPLETE at (${Date.now().toString()})}`
    )
    console.log(`Took ${((Date.now() - start) / 1000).toLocaleString('en-US')}}`)
  }, [chainId, dispatch, whitelistedVaultsByChainId])

  useEffect(() => {
    fetchVaultsByChainId().subscribe({
      next: ({ data: { vaults } }) => {
        const filteredVaults = vaults.filter((vault: VaultData) => vaultsList.find((v) => utils.getAddress(v.address) === utils.getAddress(vault.id)));
        // console.log('Filtered Vaults', filteredVaults);
        // Dispatch vaults info before fetching details
        Promise.all(filteredVaults.map((vault: VaultData): VaultDetails => (constructVaultDetails(vault))))
        .then((vaultsDetails: Array<VaultDetails>) => {
          dispatch(addVaultsDetailsByChainID({
            chainId,
            vaultsDetails
          }))
        })
        // Fetch vaults details
        Promise.all(filteredVaults.map(fetchVault))
        .then(vaultsDetails => {
          dispatch(addVaultsDetailsByChainID({
            chainId,
            vaultsDetails
          }))
          return vaultsDetails;
        })
      },
      error: (e) => console.error(e)
    })
  }, [chainId, dispatch, fetchVault, fetchVaultsByChainId, vaultsList])

  useEffect(() => {
    if(whitelistedVaultsByChainId)
      fetchVaultsUnderlyingBalances();
  }, [fetchVaultsUnderlyingBalances, whitelistedVaultsByChainId])

  return null;
}